import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import { useStaff } from "@/components/pages/staff/hooks/useStaff";
import { messagingChannelConfigsService } from "@/services/messaging-channel-configs-service";
import type { StaffWithRelations } from "@/services/staffService";
import type {
  CommunicationChannelKind,
  MessagingChannelConfig,
} from "@/types/messaging-channel-config";
import {
  DEFAULT_BOOKING_AFTER_UNIT,
  DEFAULT_BOOKING_AFTER_VALUE,
  DEFAULT_BOOKING_AUTO_SEND,
  DEFAULT_BOOKING_REMINDER_DAYS,
} from "../utils/constants";
import type {
  BookingAfterUnit,
  Channel,
  SenderType,
  StaffOption,
} from "../utils/types";
import { clampPositiveInt } from "../utils/clampPositiveInt";

type SenderPreferences = {
  senderType: SenderType;
  staffId: string;
  fallback: string;
};

type BookingTimingPreferences = {
  autoSend: boolean;
  afterValue: number;
  afterUnit: BookingAfterUnit;
  reminderDays: number;
};

const FALLBACK_BEHAVIOR_HOTEL_DEFAULT = "hotel_default";
const MESSAGE_SENDER_SETTINGS_KEY = "messageSenderSettings";
const ARRIVAL_MESSAGE_SETTINGS_KEY = "arrivalMessage";
const BOOKING_CONFIRMATION_SETTINGS_KEY = "bookingConfirmation";
const BOOKING_CONFIRMATION_TIMING_KEY = "bookingConfirmationTiming";

const DEFAULT_ARRIVAL_SENDER_PREFERENCES: SenderPreferences = {
  senderType: "staff",
  staffId: "",
  fallback: FALLBACK_BEHAVIOR_HOTEL_DEFAULT,
};

const DEFAULT_BOOKING_SENDER_PREFERENCES: SenderPreferences = {
  senderType: "hotel",
  staffId: "",
  fallback: FALLBACK_BEHAVIOR_HOTEL_DEFAULT,
};

const DEFAULT_BOOKING_TIMING_PREFERENCES: BookingTimingPreferences = {
  autoSend: DEFAULT_BOOKING_AUTO_SEND,
  afterValue: DEFAULT_BOOKING_AFTER_VALUE,
  afterUnit: DEFAULT_BOOKING_AFTER_UNIT,
  reminderDays: DEFAULT_BOOKING_REMINDER_DAYS,
};

const CHANNEL_TO_API_KIND: Record<Channel, CommunicationChannelKind> = {
  sms: "SMS",
  email: "EMAIL",
  whatsapp: "WHATSAPP",
};

const API_KIND_TO_CHANNEL: Partial<Record<CommunicationChannelKind, Channel>> =
  {
    SMS: "sms",
    EMAIL: "email",
    WHATSAPP: "whatsapp",
  };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSenderType(value: unknown): value is SenderType {
  return value === "hotel" || value === "staff";
}

function isBookingAfterUnit(value: unknown): value is BookingAfterUnit {
  return value === "minutes" || value === "hours";
}

function readPositiveInteger(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 1) {
    return fallback;
  }

  return Math.floor(value);
}

function readSenderPreferences(
  metadata: Record<string, unknown> | undefined,
  key: string,
  defaults: SenderPreferences,
): SenderPreferences {
  const messageSettings = isRecord(metadata?.[MESSAGE_SENDER_SETTINGS_KEY])
    ? metadata[MESSAGE_SENDER_SETTINGS_KEY]
    : undefined;
  const rawPreferences =
    messageSettings && isRecord(messageSettings[key])
      ? messageSettings[key]
      : undefined;

  if (!isRecord(rawPreferences)) {
    return defaults;
  }

  const senderType = isSenderType(rawPreferences.senderType)
    ? rawPreferences.senderType
    : defaults.senderType;
  const staffId =
    typeof rawPreferences.staffId === "string"
      ? rawPreferences.staffId
      : defaults.staffId;
  const fallback =
    typeof rawPreferences.fallback === "string" && rawPreferences.fallback
      ? rawPreferences.fallback
      : defaults.fallback;

  return { senderType, staffId, fallback };
}

function hasSenderPreferences(
  config: MessagingChannelConfig,
  key: string,
): boolean {
  const messageSettings = isRecord(
    config.channelMetadata?.[MESSAGE_SENDER_SETTINGS_KEY],
  )
    ? config.channelMetadata[MESSAGE_SENDER_SETTINGS_KEY]
    : undefined;

  return Boolean(messageSettings && isRecord(messageSettings[key]));
}

function readBookingTimingPreferences(
  metadata: Record<string, unknown> | undefined,
): BookingTimingPreferences {
  const rawPreferences = isRecord(metadata?.[BOOKING_CONFIRMATION_TIMING_KEY])
    ? metadata[BOOKING_CONFIRMATION_TIMING_KEY]
    : undefined;

  if (!isRecord(rawPreferences)) {
    return DEFAULT_BOOKING_TIMING_PREFERENCES;
  }

  const autoSend =
    typeof rawPreferences.autoSend === "boolean"
      ? rawPreferences.autoSend
      : DEFAULT_BOOKING_TIMING_PREFERENCES.autoSend;
  const afterValue = readPositiveInteger(
    rawPreferences.afterValue,
    DEFAULT_BOOKING_TIMING_PREFERENCES.afterValue,
  );
  const afterUnit = isBookingAfterUnit(rawPreferences.afterUnit)
    ? rawPreferences.afterUnit
    : DEFAULT_BOOKING_TIMING_PREFERENCES.afterUnit;
  const reminderDays = readPositiveInteger(
    rawPreferences.reminderDays,
    DEFAULT_BOOKING_TIMING_PREFERENCES.reminderDays,
  );

  return { autoSend, afterValue, afterUnit, reminderDays };
}

function hasBookingTimingPreferences(config: MessagingChannelConfig): boolean {
  return isRecord(config.channelMetadata?.[BOOKING_CONFIRMATION_TIMING_KEY]);
}

function writeBookingTimingPreferences(
  metadata: Record<string, unknown> | undefined,
  preferences: BookingTimingPreferences,
): Record<string, unknown> {
  return {
    ...(metadata ?? {}),
    [BOOKING_CONFIRMATION_TIMING_KEY]: preferences,
  };
}

function writeSenderPreferences(
  metadata: Record<string, unknown> | undefined,
  key: string,
  preferences: SenderPreferences,
): Record<string, unknown> {
  const baseMetadata = metadata ?? {};
  const messageSettings = isRecord(baseMetadata[MESSAGE_SENDER_SETTINGS_KEY])
    ? baseMetadata[MESSAGE_SENDER_SETTINGS_KEY]
    : {};

  return {
    ...baseMetadata,
    [MESSAGE_SENDER_SETTINGS_KEY]: {
      ...messageSettings,
      [key]: preferences,
    },
  };
}

function channelToApiKind(channel: Channel): CommunicationChannelKind {
  return CHANNEL_TO_API_KIND[channel];
}

function apiKindToChannel(channel: CommunicationChannelKind): Channel | null {
  return API_KIND_TO_CHANNEL[channel] ?? null;
}

function formatStaffName(user: StaffWithRelations): string {
  const fullName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || user.email || user.staff?.id || user.id;
}

function getInitials(name: string): string {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return initials || "ST";
}

function toStaffOption(user: StaffWithRelations): StaffOption | null {
  if (!user.staff?.id) {
    return null;
  }

  const name = formatStaffName(user);

  return {
    id: user.staff.id,
    name,
    role: user.staff.position || user.role || "—",
    phone: user.phone || user.email || "—",
    initials: getInitials(name),
  };
}

function findLatestConfigWithSenderSettings(
  configs: MessagingChannelConfig[] | undefined,
  key: string,
): MessagingChannelConfig | undefined {
  return configs
    ?.filter((config) => hasSenderPreferences(config, key))
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )[0];
}

function findLatestConfigWithBookingSettings(
  configs: MessagingChannelConfig[] | undefined,
  key: string,
): MessagingChannelConfig | undefined {
  return configs
    ?.filter(
      (config) =>
        hasSenderPreferences(config, key) ||
        hasBookingTimingPreferences(config),
    )
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )[0];
}

export function useMessagingSettingsForm() {
  const { t } = useTranslation();
  const hotelId = useCurrentHotelId();
  const queryClient = useQueryClient();
  const hasInitializedArrivalChannel = useRef(false);
  const hasInitializedBookingChannel = useRef(false);

  const [channel, setChannel] = useState<Channel>("sms");
  const [senderType, setSenderType] = useState<SenderType>(
    DEFAULT_ARRIVAL_SENDER_PREFERENCES.senderType,
  );
  const [staffId, setStaffId] = useState<string>(
    DEFAULT_ARRIVAL_SENDER_PREFERENCES.staffId,
  );
  const [fallback, setFallback] = useState<string>(
    DEFAULT_ARRIVAL_SENDER_PREFERENCES.fallback,
  );

  const [bookingAutoSend, setBookingAutoSend] = useState(
    DEFAULT_BOOKING_AUTO_SEND,
  );
  const [bookingAfterValue, setBookingAfterValue] = useState(
    DEFAULT_BOOKING_AFTER_VALUE,
  );
  const [bookingAfterUnit, setBookingAfterUnit] = useState<BookingAfterUnit>(
    DEFAULT_BOOKING_AFTER_UNIT,
  );
  const [bookingReminderDays, setBookingReminderDays] = useState(
    DEFAULT_BOOKING_REMINDER_DAYS,
  );
  const [bookingChannel, setBookingChannel] = useState<Channel>("email");
  const [bookingSenderType, setBookingSenderType] = useState<SenderType>(
    DEFAULT_BOOKING_SENDER_PREFERENCES.senderType,
  );
  const [bookingStaffId, setBookingStaffId] = useState<string>(
    DEFAULT_BOOKING_SENDER_PREFERENCES.staffId,
  );
  const [bookingFallback, setBookingFallback] = useState<string>(
    DEFAULT_BOOKING_SENDER_PREFERENCES.fallback,
  );

  const configsQuery = useQuery({
    queryKey: ["messaging-channel-configs", hotelId],
    queryFn: () => messagingChannelConfigsService.list(hotelId!),
    enabled: Boolean(hotelId),
  });

  const staffQuery = useStaff({
    hotelId: hotelId ?? undefined,
    isActive: true,
    perPage: 100,
  });

  const staffOptions = useMemo(
    () =>
      staffQuery.data?.users
        .map(toStaffOption)
        .filter((option): option is StaffOption => option !== null) ?? [],
    [staffQuery.data],
  );

  const selectedStaff = useMemo(
    () => staffOptions.find((s) => s.id === staffId),
    [staffId, staffOptions],
  );

  const selectedArrivalConfig = useMemo(() => {
    const apiChannel = channelToApiKind(channel);

    return configsQuery.data?.find((config) => config.channel === apiChannel);
  }, [channel, configsQuery.data]);

  const selectedBookingConfig = useMemo(() => {
    const apiChannel = channelToApiKind(bookingChannel);

    return configsQuery.data?.find((config) => config.channel === apiChannel);
  }, [bookingChannel, configsQuery.data]);

  const selectedBookingStaff = useMemo(
    () => staffOptions.find((s) => s.id === bookingStaffId),
    [bookingStaffId, staffOptions],
  );

  const applyArrivalPreferences = useCallback(
    (config: MessagingChannelConfig | undefined) => {
      const senderPreferences = readSenderPreferences(
        config?.channelMetadata,
        ARRIVAL_MESSAGE_SETTINGS_KEY,
        DEFAULT_ARRIVAL_SENDER_PREFERENCES,
      );

      setSenderType(senderPreferences.senderType);
      setStaffId(senderPreferences.staffId);
      setFallback(senderPreferences.fallback);
    },
    [],
  );

  const applyBookingPreferences = useCallback(
    (config: MessagingChannelConfig | undefined) => {
      const senderPreferences = readSenderPreferences(
        config?.channelMetadata,
        BOOKING_CONFIRMATION_SETTINGS_KEY,
        DEFAULT_BOOKING_SENDER_PREFERENCES,
      );
      const timingPreferences = readBookingTimingPreferences(
        config?.channelMetadata,
      );

      setBookingSenderType(senderPreferences.senderType);
      setBookingStaffId(senderPreferences.staffId);
      setBookingFallback(senderPreferences.fallback);
      setBookingAutoSend(timingPreferences.autoSend);
      setBookingAfterValue(timingPreferences.afterValue);
      setBookingAfterUnit(timingPreferences.afterUnit);
      setBookingReminderDays(timingPreferences.reminderDays);
    },
    [],
  );

  useEffect(() => {
    if (hasInitializedArrivalChannel.current || !configsQuery.data) {
      return;
    }

    hasInitializedArrivalChannel.current = true;

    const latestArrivalConfig = findLatestConfigWithSenderSettings(
      configsQuery.data,
      ARRIVAL_MESSAGE_SETTINGS_KEY,
    );
    const latestArrivalChannel = latestArrivalConfig
      ? apiKindToChannel(latestArrivalConfig.channel)
      : null;

    if (latestArrivalChannel) {
      setChannel(latestArrivalChannel);
      return;
    }

    applyArrivalPreferences(selectedArrivalConfig);
  }, [applyArrivalPreferences, configsQuery.data, selectedArrivalConfig]);

  useEffect(() => {
    applyArrivalPreferences(selectedArrivalConfig);
  }, [applyArrivalPreferences, selectedArrivalConfig]);

  useEffect(() => {
    if (hasInitializedBookingChannel.current || !configsQuery.data) {
      return;
    }

    hasInitializedBookingChannel.current = true;

    const latestBookingConfig = findLatestConfigWithBookingSettings(
      configsQuery.data,
      BOOKING_CONFIRMATION_SETTINGS_KEY,
    );
    const latestBookingChannel = latestBookingConfig
      ? apiKindToChannel(latestBookingConfig.channel)
      : null;

    if (latestBookingChannel) {
      setBookingChannel(latestBookingChannel);
      return;
    }

    applyBookingPreferences(selectedBookingConfig);
  }, [applyBookingPreferences, configsQuery.data, selectedBookingConfig]);

  useEffect(() => {
    applyBookingPreferences(selectedBookingConfig);
  }, [applyBookingPreferences, selectedBookingConfig]);

  useEffect(() => {
    if (senderType === "staff" && !staffId && staffOptions[0]) {
      setStaffId(staffOptions[0].id);
    }
  }, [senderType, staffId, staffOptions]);

  useEffect(() => {
    if (bookingSenderType === "staff" && !bookingStaffId && staffOptions[0]) {
      setBookingStaffId(staffOptions[0].id);
    }
  }, [bookingSenderType, bookingStaffId, staffOptions]);

  const handleArrivalCancel = useCallback(() => {
    applyArrivalPreferences(selectedArrivalConfig);
  }, [applyArrivalPreferences, selectedArrivalConfig]);

  const handleBookingCancel = useCallback(() => {
    applyBookingPreferences(selectedBookingConfig);
  }, [applyBookingPreferences, selectedBookingConfig]);

  const onBookingAfterValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setBookingAfterValue((prev) => clampPositiveInt(e.target.value, prev));
    },
    [],
  );

  const onReminderDaysChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setBookingReminderDays((prev) => clampPositiveInt(e.target.value, prev));
    },
    [],
  );

  const arrivalSaveMutation = useMutation({
    mutationFn: async () => {
      if (!hotelId) {
        throw new Error(
          t("layout.messagingSettingsPage.hotelNotSelected", {
            defaultValue: "Hotel is not selected",
          }),
        );
      }

      const apiChannel = channelToApiKind(channel);
      const existingConfig =
        selectedArrivalConfig ??
        configsQuery.data?.find((config) => config.channel === apiChannel);
      const preferences: SenderPreferences = {
        senderType,
        staffId: senderType === "staff" ? staffId : "",
        fallback,
      };

      return messagingChannelConfigsService.upsert(hotelId, apiChannel, {
        enabled: existingConfig?.enabled ?? false,
        isDefault: existingConfig?.isDefault,
        channelMetadata: writeSenderPreferences(
          existingConfig?.channelMetadata,
          ARRIVAL_MESSAGE_SETTINGS_KEY,
          preferences,
        ),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["messaging-channel-configs", hotelId],
      });
      toast.success(t("layout.messagingSettingsPage.saveSuccess"));
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : t("layout.messagingSettingsPage.saveError", {
              defaultValue: "Failed to save messaging settings",
            });

      toast.error(message);
    },
  });

  const handleArrivalSave = useCallback(() => {
    arrivalSaveMutation.mutate();
  }, [arrivalSaveMutation]);

  const bookingSaveMutation = useMutation({
    mutationFn: async () => {
      if (!hotelId) {
        throw new Error(
          t("layout.messagingSettingsPage.hotelNotSelected", {
            defaultValue: "Hotel is not selected",
          }),
        );
      }

      const apiChannel = channelToApiKind(bookingChannel);
      const existingConfig =
        selectedBookingConfig ??
        configsQuery.data?.find((config) => config.channel === apiChannel);
      const preferences: SenderPreferences = {
        senderType: bookingSenderType,
        staffId: bookingSenderType === "staff" ? bookingStaffId : "",
        fallback: bookingFallback,
      };
      const timingPreferences: BookingTimingPreferences = {
        autoSend: bookingAutoSend,
        afterValue: bookingAfterValue,
        afterUnit: bookingAfterUnit,
        reminderDays: bookingReminderDays,
      };
      const metadataWithTiming = writeBookingTimingPreferences(
        existingConfig?.channelMetadata,
        timingPreferences,
      );

      return messagingChannelConfigsService.upsert(hotelId, apiChannel, {
        enabled: existingConfig?.enabled ?? false,
        isDefault: existingConfig?.isDefault,
        channelMetadata: writeSenderPreferences(
          metadataWithTiming,
          BOOKING_CONFIRMATION_SETTINGS_KEY,
          preferences,
        ),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["messaging-channel-configs", hotelId],
      });
      toast.success(t("layout.messagingSettingsPage.saveSuccess"));
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : t("layout.messagingSettingsPage.saveError", {
              defaultValue: "Failed to save messaging settings",
            });

      toast.error(message);
    },
  });

  const handleBookingSave = useCallback(() => {
    bookingSaveMutation.mutate();
  }, [bookingSaveMutation]);

  return {
    channel,
    setChannel,
    senderType,
    setSenderType,
    staffId,
    setStaffId,
    fallback,
    setFallback,
    bookingChannel,
    setBookingChannel,
    bookingSenderType,
    setBookingSenderType,
    bookingStaffId,
    setBookingStaffId,
    bookingFallback,
    setBookingFallback,
    bookingAutoSend,
    setBookingAutoSend,
    bookingAfterValue,
    bookingAfterUnit,
    setBookingAfterUnit,
    onBookingAfterValueChange,
    bookingReminderDays,
    onReminderDaysChange,
    selectedStaff,
    selectedBookingStaff,
    staffOptions,
    handleArrivalCancel,
    handleArrivalSave,
    handleBookingCancel,
    handleBookingSave,
    isArrivalSavePending: arrivalSaveMutation.isPending,
    isBookingSavePending: bookingSaveMutation.isPending,
  };
}
